import { lightTheme, Colors } from '@unmind/design-system/theme/deprecated';
import { GridColumns, GridGutters, GridMargins } from './grid';
import ZIndex from './zIndex';

const TELUS_COLORS = {
  purple: '#4B286B',
  purpleLinen: '#F2EFF4',
  purple100: 'rgba(75, 40, 107, 0.1)',
  purple200: '#e3dfe9',
  purple300: '#c1b8cd',
  purple600: 'rgba(75, 40, 107, 0.6)',
  purple800: 'rgba(75, 40, 107, 0.8)',
  backgroundGrey: '#F4F4F7',
  charcoal: '#424547',
  shuttle: '#686E71',
  thunder: '#2C2E30',
  icon: '#CDBADE',
  iconBackground: '#EDDFFB',
  cardImageBackground: '#D8CBE5',
  novaLogoCenter: '#B360FF',
  novaLogoOuter: '#28113D',
  novaLogoIntermediate: '#4B286B',
  red: '#C12335',
  orange: '#D16E0A',
  green: '#2B8000',
  outline: '#2681E1',
};

// Implementing this way for the Telus prototype only.

export const telusPrototypeTheme = {
  ...lightTheme,
  grid: {
    columns: GridColumns,
    gutters: GridGutters,
    margins: GridMargins,
  },
  zIndex: ZIndex,
  colors: {
    ...lightTheme.colors,

    primary: TELUS_COLORS.purple,

    outline: {
      focus: TELUS_COLORS.outline,
    },

    background: {
      ...lightTheme.colors.background,
      primary: TELUS_COLORS.backgroundGrey,
      card: Colors.white,
      cardHighlighted: TELUS_COLORS.purpleLinen,
    },
    brand: {
      primary: TELUS_COLORS.purple,
    },

    text: {
      ...lightTheme.colors.text,
      link: TELUS_COLORS.purple,
      linkHover: Colors.glassDark900,
    },

    border: {
      ...lightTheme.colors.border,
      primary: TELUS_COLORS.thunder,
    },

    multiSelect: {
      multiValue: {
        backgroundColor: lightTheme.colors.form.field.background,
      },
      multiValueRemove: {
        backgroundColor: lightTheme.colors.form.field.background,
        focused: {
          backgroundColor: lightTheme.colors.form.dropdown.remove,
        },
      },
      option: {
        hover: {
          backgroundColor:
            lightTheme.colors.form.dropdown.option.background.focus,
        },
        active: {
          backgroundColor:
            lightTheme.colors.form.dropdown.option.background.focus,
        },
        backgroundColor:
          lightTheme.colors.form.dropdown.option.background.default,
      },
    },

    chatbot: {
      logo: {
        primary: TELUS_COLORS.novaLogoCenter,
        secondary: TELUS_COLORS.novaLogoIntermediate,
        tertiary: TELUS_COLORS.novaLogoOuter,
      },
      messages: {
        user: {
          background: Colors.white,
        },
        loading: {
          bubbles: TELUS_COLORS.purple,
        },
      },
    },
    index: {
      bands: {
        low: TELUS_COLORS.red,
        mid: TELUS_COLORS.orange,
        high: TELUS_COLORS.green,
      },
    },
    mood: {
      saddest: Colors.sentimentOrange200,
      sadder: '#FDBD43',
      sad: '#FFD78A',
      neutral: '#86B6FF',
      happy: '#72F270',
      happier: '#5EDB5C',
      happiest: Colors.sentimentGreen200,
      border: Colors.brown500,
    },
    mediaPlayer: {
      audio: {
        icon: {
          default: Colors.glassDark900,
          hover: Colors.glassDark700,
          background: Colors.glassDark200,
        },
        backButton: {
          hover: Colors.glassDark100,
        },
        borderFocus: Colors.blue700,

        animation: {
          page: TELUS_COLORS.backgroundGrey,

          bg_1: TELUS_COLORS.purple,
          stroke_1: TELUS_COLORS.purple,

          bg_2: TELUS_COLORS.purple300,
          stroke_2: TELUS_COLORS.purple,

          bg_3: TELUS_COLORS.purple200,
          stroke_3: TELUS_COLORS.purple,

          bg_4: TELUS_COLORS.purple,
          stroke_4: TELUS_COLORS.purple,

          bg_5: TELUS_COLORS.purple300,
          stroke_5: TELUS_COLORS.purple,
        },
      },
    },
    toggle: {
      ...lightTheme.colors.toggle,
      on: TELUS_COLORS.purple,
    },

    form: {
      ...lightTheme.colors.form,
      field: {
        background: Colors.white,
        text: TELUS_COLORS.thunder,
      },
      checkbox: {
        ...lightTheme.colors.form.checkbox,
        check: Colors.white,
      },
    },

    meta: {
      theme: TELUS_COLORS.backgroundGrey,
      tile: TELUS_COLORS.purple,
      icon: TELUS_COLORS.backgroundGrey,
    },

    contentTile: {
      imageFallback: {
        background: TELUS_COLORS.cardImageBackground,
        iconBackground: TELUS_COLORS.iconBackground,
        iconBorder: TELUS_COLORS.cardImageBackground,
        iconColor: TELUS_COLORS.icon,
      },
      imageButton: {
        background: '#FFFFFF',
        foreground: TELUS_COLORS.purple,
      },
    },

    mainNavigation: {
      tab: {
        active: {
          text: TELUS_COLORS.purple,
          icon: TELUS_COLORS.purple,
          background: TELUS_COLORS.purpleLinen,
        },
        inactive: {
          text: TELUS_COLORS.charcoal,
          icon: TELUS_COLORS.shuttle,
        },
      },
    },

    calendar: {
      ...lightTheme.colors.calendar,
      selected: {
        background: TELUS_COLORS.purple,
        color: Colors.white,
      },
      today: {
        background: TELUS_COLORS.purple100,
        color: Colors.glassDark900,
      },
    },
  },
  button: {
    ...lightTheme.button,
    primary: {
      ...lightTheme.button.primary,
      default: {
        ...lightTheme.button.primary.default,
        hover: {
          backgroundColor: TELUS_COLORS.purple800,
        },
        backgroundColor: TELUS_COLORS.purple,
        borderColor: TELUS_COLORS.purple,
      },
      disabled: {
        ...lightTheme.button.primary.disabled,
        backgroundColor: TELUS_COLORS.purple300,
        borderColor: TELUS_COLORS.purple300,
        textColor: TELUS_COLORS.purple600,
      },
    },
    secondary: {
      ...lightTheme.button.secondary,
      default: {
        ...lightTheme.button.secondary.default,
        borderColor: TELUS_COLORS.purple,
        textColor: TELUS_COLORS.purple,
        hover: {
          backgroundColor: TELUS_COLORS.purple100,
        },
      },
      disabled: {
        ...lightTheme.button.secondary.disabled,
        borderColor: TELUS_COLORS.purple300,
        textColor: TELUS_COLORS.purple300,
      },
    },
    borderFocusColor: TELUS_COLORS.purple,
  },
};

export default telusPrototypeTheme;
